import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Grid, Card, CardContent, Typography, Button,
  TextField, Box, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Toolbar,
  Slider, FormControlLabel, Checkbox, Collapse,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon, Search as SearchIcon,
  Edit as EditIcon, Refresh as GenerateIcon, TuneOutlined,
} from '@mui/icons-material';
import Navbar from '../components/Navbar';
import PasswordStrength from '../components/PasswordStrength';
import { api } from '../api';
import { useTheme } from '../styles/Theme';
import '../styles/styles.css';

const Dashboard = ({ notificationsRef }) => {
  const [passwords, setPasswords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState({ title: '', username: '', password: '', url: '' });
  const [editingPassword, setEditingPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [genOptions, setGenOptions] = useState({ length: 16, useUppercase: true, useNumbers: true, useSpecial: true });
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const textColor = darkMode ? '#f5f5f7' : '#1d1d1f';
  const secondaryTextColor = darkMode ? '#aeaeb2' : '#6e6e73';

  const messages = {
    copyPasswordSuccess: 'Copied to clipboard.',
    copyPasswordError: 'Could not copy.',
    deletePasswordSuccess: 'Password deleted.',
    savePasswordSuccess: 'Saved.',
    allFieldsRequired: 'Please fill in all fields.',
    serverError: 'Something went wrong. Try again.',
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    if (!token || !username) { navigate('/'); return; }
    setIsAuthenticated(true);
    fetchPasswords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchPasswords = async () => {
    try {
      const data = await api.getPasswords();
      setPasswords(data);
    } catch (err) {
      notificationsRef.current.showSnackbar(messages.serverError, 'error');
    }
  };

  const handleCopyPassword = async (password) => {
    try {
      await navigator.clipboard.writeText(password);
      notificationsRef.current.showSnackbar(messages.copyPasswordSuccess, 'success');
    } catch {
      notificationsRef.current.showSnackbar(messages.copyPasswordError, 'error');
    }
  };

  const togglePasswordVisibility = (passwordId) => {
    setVisiblePasswords((prev) => ({ ...prev, [passwordId]: !prev[passwordId] }));
  };

  const handleDeletePassword = async (passwordId) => {
    try {
      await api.deletePassword(passwordId);
      setPasswords((prev) => prev.filter((p) => p.id !== passwordId));
      notificationsRef.current.showSnackbar(messages.deletePasswordSuccess, 'success');
    } catch {
      notificationsRef.current.showSnackbar(messages.serverError, 'error');
    }
  };

  const handleAddPassword = () => {
    setNewPassword({ title: '', username: '', password: '', url: '' });
    setEditingPassword(null);
    setDialogOpen(true);
    setIsSaveEnabled(false);
  };

  const handleEditPassword = (password) => {
    setNewPassword({ title: password.title, username: password.username, password: password.password, url: password.url || '' });
    setEditingPassword(password);
    setDialogOpen(true);
    setIsSaveEnabled(false);
  };

  const handleSavePassword = async () => {
    if (!newPassword.title || !newPassword.username || !newPassword.password) {
      notificationsRef.current.showSnackbar(messages.allFieldsRequired, 'warning');
      return;
    }
    try {
      if (editingPassword) {
        await api.updatePassword(editingPassword.id, { title: newPassword.title, username: newPassword.username, password: newPassword.password, url: newPassword.url || '' });
        setPasswords((prev) => prev.map((p) => p.id === editingPassword.id ? { ...p, ...newPassword } : p));
      } else {
        const created = await api.createPassword({ title: newPassword.title, username: newPassword.username, password: newPassword.password, url: newPassword.url || '' });
        setPasswords((prev) => [created, ...prev]);
      }
      notificationsRef.current.showSnackbar(messages.savePasswordSuccess, 'success');
      setDialogOpen(false);
      setEditingPassword(null);
      setIsSaveEnabled(false);
    } catch {
      notificationsRef.current.showSnackbar(messages.serverError, 'error');
    }
  };

  const handleChange = (field, value) => {
    setNewPassword((prev) => ({ ...prev, [field]: value }));
    setIsSaveEnabled(true);
  };

  const filteredPasswords = passwords.filter((p) =>
    (p.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (p.username?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  if (!isAuthenticated) return null;

  return (
    <Box className="dashContainer">
      <Navbar />
      <Container component="main" maxWidth="xl" className="dashMain">
        <Toolbar sx={{ minHeight: { xs: '64px', sm: '80px' } }} />
        <Box sx={{ mt: { xs: 1, sm: 0 } }}>
        <Box className="dashHeader">
          <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={handleAddPassword}>
            Add Password
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box className="passHeader">
                  <Typography variant="h5" style={{ color: textColor }}>
                    Passwords
                    <Chip label={filteredPasswords.length} size="small" className="passCount" />
                  </Typography>
                  <TextField
                    fullWidth placeholder="Search passwords..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} className="searchField"
                    InputProps={{ startAdornment: <SearchIcon style={{ color: secondaryTextColor, marginRight: 8 }} /> }}
                  />
                </Box>

                {filteredPasswords.length === 0 ? (
                  <Typography variant="h6" style={{ color: secondaryTextColor }} align="center">No passwords found</Typography>
                ) : (
                  filteredPasswords.map((password) => (
                    <Card key={password.id} className="passCard">
                      <CardContent>
                        <Box className="passDetails">
                          <Box>
                            <Typography variant="h6" style={{ color: textColor }}>{password.title}</Typography>
                            {password.url && (
                              <Typography variant="caption" component="a"
                                href={password.url.startsWith('http') ? password.url : `https://${password.url}`}
                                target="_blank" rel="noopener noreferrer"
                                style={{ color: darkMode ? '#2997ff' : '#0071e3', textDecoration: 'none', display: 'block' }}>
                                {password.url}
                              </Typography>
                            )}
                            {visiblePasswords[password.id] && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="body2" style={{ color: secondaryTextColor }}>
                                  <span style={{ fontWeight: 600 }}>Username:</span> {password.username}
                                </Typography>
                                <Typography variant="body2" style={{ color: secondaryTextColor }}>
                                  <span style={{ fontWeight: 600 }}>Password:</span> {password.password}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box className="passActions">
                            <Tooltip title={visiblePasswords[password.id] ? 'Hide' : 'Show'}>
                              <IconButton onClick={() => togglePasswordVisibility(password.id)} size="small">
                                {visiblePasswords[password.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton onClick={() => handleEditPassword(password)} size="small"><EditIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="Copy">
                              <IconButton onClick={() => handleCopyPassword(password.password)} size="small"><CopyIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => handleDeletePassword(password.id)} size="small" color="error"><DeleteIcon /></IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <PasswordStrength password={password.password} />
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setShowGenerator(false); }} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: '20px', padding: '8px', boxShadow: '0 8px 40px rgba(0,0,0,0.12)', margin: '24px', maxHeight: 'calc(100vh - 48px)', overflow: 'visible' } }}
          sx={{ '& .MuiDialog-paper': { alignSelf: 'center' } }}>
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', letterSpacing: '-0.3px', pb: 0 }}>
            {editingPassword ? 'Edit Password' : 'New Password'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2, overflow: 'visible' }}>
            <TextField autoFocus margin="dense" label="Title" type="text" fullWidth value={newPassword.title} onChange={(e) => handleChange('title', e.target.value)} />
            <TextField margin="dense" label="Username" type="text" fullWidth value={newPassword.username} onChange={(e) => handleChange('username', e.target.value)} />
            <TextField margin="dense" label="URL (optional)" type="url" fullWidth value={newPassword.url} onChange={(e) => handleChange('url', e.target.value)} placeholder="https://example.com" />
            <TextField
              margin="dense" label="Password"
              type={showPassword ? 'text' : 'password'} fullWidth
              value={newPassword.password} onChange={(e) => handleChange('password', e.target.value)}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Copy password">
                      <span>
                        <IconButton onClick={async () => { if (!newPassword.password) return; await navigator.clipboard.writeText(newPassword.password); notificationsRef.current.showSnackbar('Copied to clipboard.', 'success'); }} size="small" disabled={!newPassword.password}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Quick generate">
                      <IconButton onClick={() => {
                        const lower = 'abcdefghijklmnopqrstuvwxyz';
                        const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
                        const nums = '0123456789';
                        const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
                        const all = lower + upper + nums + special;
                        let pwd = lower[Math.floor(Math.random() * lower.length)] + upper[Math.floor(Math.random() * upper.length)] + nums[Math.floor(Math.random() * nums.length)] + special[Math.floor(Math.random() * special.length)];
                        while (pwd.length < 16) pwd += all[Math.floor(Math.random() * all.length)];
                        handleChange('password', pwd.split('').sort(() => Math.random() - 0.5).join('').slice(0, 16));
                        setShowPassword(true);
                      }} size="small">
                        <GenerateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Advanced options">
                      <IconButton onClick={() => setShowGenerator(p => !p)} size="small" color={showGenerator ? 'primary' : 'default'}>
                        <TuneOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton onClick={() => setShowPassword((p) => !p)} size="small">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Box>
                ),
              }}
            />
            <PasswordStrength password={newPassword.password} />

            <Collapse in={showGenerator}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Generator</Typography>
                <Typography variant="caption" color="text.secondary">Length: {genOptions.length}</Typography>
                <Slider
                  value={genOptions.length} min={8} max={32} size="small"
                  onChange={(_, v) => setGenOptions(o => ({ ...o, length: v }))}
                  marks={[{ value: 8, label: '8' }, { value: 16, label: '16' }, { value: 24, label: '24' }, { value: 32, label: '32' }]}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  <FormControlLabel control={<Checkbox size="small" checked={genOptions.useUppercase} onChange={e => setGenOptions(o => ({ ...o, useUppercase: e.target.checked }))} />} label={<Typography variant="caption">Uppercase</Typography>} />
                  <FormControlLabel control={<Checkbox size="small" checked={genOptions.useNumbers} onChange={e => setGenOptions(o => ({ ...o, useNumbers: e.target.checked }))} />} label={<Typography variant="caption">Numbers</Typography>} />
                  <FormControlLabel control={<Checkbox size="small" checked={genOptions.useSpecial} onChange={e => setGenOptions(o => ({ ...o, useSpecial: e.target.checked }))} />} label={<Typography variant="caption">Special chars</Typography>} />
                </Box>
                <Button fullWidth size="small" variant="outlined"
                  onClick={() => {
                    const lower = 'abcdefghijklmnopqrstuvwxyz';
                    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
                    const nums = '0123456789';
                    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
                    let chars = lower;
                    let pwd = lower[Math.floor(Math.random() * lower.length)];
                    if (genOptions.useUppercase) { chars += upper; pwd += upper[Math.floor(Math.random() * upper.length)]; }
                    if (genOptions.useNumbers) { chars += nums; pwd += nums[Math.floor(Math.random() * nums.length)]; }
                    if (genOptions.useSpecial) { chars += special; pwd += special[Math.floor(Math.random() * special.length)]; }
                    while (pwd.length < genOptions.length) pwd += chars[Math.floor(Math.random() * chars.length)];
                    const generated = pwd.split('').sort(() => Math.random() - 0.5).join('').slice(0, genOptions.length);
                    handleChange('password', generated);
                    setShowPassword(true);
                  }}
                  sx={{
                    mt: 1, borderRadius: '8px', textTransform: 'none',
                    borderColor: darkMode ? '#aeaeb2' : undefined,
                    color: darkMode ? '#aeaeb2' : undefined,
                    '&:hover': {
                      borderColor: darkMode ? '#f5f5f7' : undefined,
                      color: darkMode ? '#f5f5f7' : undefined,
                      backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : undefined,
                    }
                  }}>
                  Generate
                </Button>
              </Box>
            </Collapse>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => { setDialogOpen(false); setShowGenerator(false); }} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 500, color: 'text.secondary' }}>Cancel</Button>
            <Button onClick={handleSavePassword} variant="contained" disabled={!isSaveEnabled} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 500, boxShadow: 'none', px: 3 }}>Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;